
import { supabase } from "@/integrations/supabase/client";

/**
 * Generates embeddings for text content and associates it with an entry
 * @param text The text content to generate embeddings for
 * @param id The ID of the knowledge entry
 */
export async function generateEmbeddings(text: string, id: string) {
  console.log('Starting embedding generation:', {
    id,
    textLength: text.length
  });

  try {
    // Use the knowledge-management edge function to update the entry
    const { data, error } = await supabase.functions.invoke('knowledge-management', {
      method: 'PUT',
      body: { 
        id,
        content: text,
        operation: 'generate_embeddings'
      }
    });

    if (error) {
      console.error('Error generating embeddings:', error);
      throw new Error('Failed to generate embeddings');
    }

    console.log('Embedding generation successful:', data);
    return data;
  } catch (error) {
    console.error('Exception generating embeddings:', error);
    throw error;
  }
}

/**
 * Processes a document by uploading it to a vector store and generating embeddings
 * @param fileUrl The URL of the file to process
 * @param entryId The ID of the knowledge entry
 */
export async function processDocument(fileUrl: string, entryId: string): Promise<{
  success: boolean;
  error?: string;
  vectorStoreId?: string;
}> {
  console.log('[DEBUG] Starting document processing with params:', { fileUrl, entryId });

  try {
    // Fetch the entry to get all relevant information including notes
    console.log('[DEBUG] Fetching entry data for ID:', entryId);
    const { data: entryData, error: entryError } = await supabase
      .from('knowledge_entries')
      .select('*')
      .eq('id', entryId)
      .single();

    if (entryError || !entryData) {
      console.error('[DEBUG] Error retrieving entry:', entryError);
      throw new Error('Failed to retrieve entry');
    }
    
    console.log('[DEBUG] Entry data retrieved successfully:', entryData);

    // Pre-function call log
    console.log('[DEBUG] About to invoke knowledge-management edge function with params:', { 
      id: entryId,
      file_url: fileUrl,
      operation: 'process_document',
      metadata: {
        title: entryData.title,
        tags: entryData.tags || [],
        notes: entryData.notes || ''
      }
    });

    // Use the knowledge-management edge function to process the document
    // Include both tags and notes in the metadata
    console.log('[DEBUG] Calling knowledge-management edge function...');
    const functionResponse = await supabase.functions.invoke('knowledge-management', {
      method: 'PUT',
      body: { 
        id: entryId,
        file_url: fileUrl,
        operation: 'process_document',
        metadata: {
          title: entryData.title,
          tags: entryData.tags || [],
          notes: entryData.notes || '' // Include notes in the metadata
        }
      }
    });
    
    console.log('[DEBUG] Edge function response received:', functionResponse);
    
    const { data, error } = functionResponse;

    if (error) {
      console.error('[DEBUG] Error from knowledge-management edge function:', error);
      console.error('[DEBUG] Error details:', JSON.stringify(error, null, 2));
      throw new Error(`Document processing failed: ${error.message}`);
    }

    if (!data) {
      console.warn('[DEBUG] Knowledge-management returned success but no data');
    } else {
      console.log('[DEBUG] Document processing data:', data);
    }

    console.log('[DEBUG] Document processing completed successfully:', data);

    return {
      success: true,
      vectorStoreId: data?.vectorStoreId
    };
  } catch (error) {
    console.error('[DEBUG] Document processing exception:', error);
    console.error('[DEBUG] Stack trace:', error instanceof Error ? error.stack : 'No stack trace');
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during document processing'
    };
  }
}

/**
 * Creates a vector store for knowledge entries
 * @param name The name of the vector store
 * @param expiresAfterDays Optional number of days after which the store expires
 */
export async function createVectorStore(name: string, expiresAfterDays?: number): Promise<{
  success: boolean;
  vectorStoreId?: string;
  error?: string;
}> {
  try {
    const { data, error } = await supabase.functions.invoke('knowledge-management', {
      method: 'POST',
      body: { 
        operation: 'create_vector_store',
        name,
        expires_after: expiresAfterDays ? {
          anchor: "last_active_at",
          days: expiresAfterDays
        } : undefined
      }
    });

    if (error) {
      console.error('Error creating vector store:', error);
      throw new Error(`Vector store creation failed: ${error.message}`);
    }

    return {
      success: true,
      vectorStoreId: data?.vectorStoreId
    };
  } catch (error) {
    console.error('Vector store creation error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error creating vector store'
    };
  }
}

/**
 * Adds a file to a vector store
 * @param vectorStoreId The ID of the vector store
 * @param fileId The ID of the file to add
 * @param attributes Optional metadata attributes to associate with the file
 */
export async function addFileToVectorStore(
  vectorStoreId: string, 
  fileId: string,
  attributes?: Record<string, any>
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { data, error } = await supabase.functions.invoke('knowledge-management', {
      method: 'POST',
      body: { 
        operation: 'add_file_to_vector_store',
        vector_store_id: vectorStoreId,
        file_id: fileId,
        attributes
      }
    });

    if (error) {
      console.error('Error adding file to vector store:', error);
      throw new Error(`Failed to add file to vector store: ${error.message}`);
    }

    return {
      success: true
    };
  } catch (error) {
    console.error('Error adding file to vector store:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error adding file to vector store'
    };
  }
}
